import React, { useEffect, useState } from 'react';
import { Grid,
    Box,
    Typography,
    Pagination, 
    Table, 
    TableBody, 
    TableCell, 
    TableContainer, 
    TableHead, 
    TableRow, 
    Paper,
    Avatar,
    TableSortLabel, 
    IconButton,
    InputBase } from '@mui/material';
import PhysiciansProfile from '../physicianProfile';
import { CustomAccordion, CustomButton, CustomModal, TableRowsSkeleton } from '../../../shared';
import { ArrowLeftIcon, ArrowRightIcon,User } from '../../../../assets';
import { useDispatch, useSelector } from 'react-redux';
import { getPhysiciansList } from '../../../../redux/thunks/physiciansThunk';
import { actions } from '../../../../redux/reducers/physicianReducer';
import SearchIcon from '@mui/icons-material/Search';
import { getSortedData } from '../../../../utility';

function PhysiciansTable() {
    const [showPhysicianModal, setShowPhysicianModal] = useState(false);
    const [position,setPosition] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [searchValue, setSearchValue] = useState('');
    const dispatch = useDispatch();
    const { company } = useSelector(state => state.company);
    const {
        physicianData, isLoading, totalPages, selectedNPI 
    } = useSelector(state => state.physician);
    const [order, setOrder] = useState('asc');
    const [orderBy, setOrderBy] = useState(null);

    const handleRequestSort = (property) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };
    const createSortHandler = (property) => () => {
        handleRequestSort(property);
    };

    const sortedData = React.useMemo(() => getSortedData(orderBy, order, physicianData), [order, orderBy, physicianData]); 
    const handlePhysicianProfileModal = (npiId) => {
        dispatch(actions.setSelectedNPI({ npiId: npiId }));
        handleModal();
    };

    const handleModal = () => {
        setShowPhysicianModal(!showPhysicianModal);
    };


    const handlePageChange = (event, page) => {
        const position = (page * 50) - 50;
        setPosition(position);
        setCurrentPage(page);
    };
    
    const handleNextClick = () => {
        if (currentPage < totalPages) {
            handlePageChange(_, currentPage + 1);
        }
    };
    
    const handlePreviousClick = () => {
        if (currentPage > 1) {
            handlePageChange(_, currentPage - 1);
        }
    };

    useEffect(() => {
        if (!company.id) return;
    
        let timeoutId;
    
        if (searchValue === '') {
            dispatch(getPhysiciansList({
                companyId: company.id,
                position: position,
                page_size: 50,
                type: 'table'
            }));
        } else if (searchValue.length > 3) {
            timeoutId = setTimeout(() => {
                dispatch(getPhysiciansList({
                    companyId: company.id,
                    position: position,
                    page_size: 50,
                    searchValue: searchValue,
                    type: 'table'
                }));
            }, 1000);
        }
    
        return () => {
            if (timeoutId) clearTimeout(timeoutId);
        };
    }, [company.id, position, searchValue]);
    


    const renderNoPhysicianData = () => (
        <TableRow>
            <TableCell colSpan={5}>
                <Box p={3} textAlign="center">
                    <Typography className="text-sm-regular gray-900">
                            No Physician data to display
                    </Typography>
                </Box>
            </TableCell>
        </TableRow>
    );

    const renderPhysicianData = () => physicianData?.length > 0
        ? sortedData.map((physician, index) => (
            <>
                <TableRow className={index % 2 === 0 ? 'odd-row-background ' : ''} key={physician?.npi_id}>
                    <TableCell className='table-row-data no-border' rowSpan={2}>
                        {physician?.npi_id}
                    </TableCell>
                    <TableCell className='no-border' rowSpan={2}>
                        <Box className="flex-row-start-center gap-12">
                            <Box className="padding-10px">
                                {/* <img src={UserIcon} width={'20px'} height={'20px'}/> */}
                                <Avatar
                                    alt={physician?.name}
                                    className="physician-profile-avatar"
                                    height={'25px'}
                                    src={`https://pfiq-v1-dev.s3.us-west-2.amazonaws.com/Rx_images/${physician?.npi_id}.jpg`}
                                    width={'25px'}
                                />
                            </Box>
                            <Typography className="text-sm-medium gray-900">
                                {physician?.name}
                            </Typography>
                        </Box>
                    </TableCell>
                    {/* Location and District */}
                    <TableCell className='table-row-data no-table-border-cell gray-900 no-border w-24' rowSpan={1} >
                        {physician?.location}
                    </TableCell>
                    <TableCell className='table-row-data no-border' rowSpan={2}>
                        <Box className="npi-table-segmentation-cell">
                            {
                                Array.isArray(physician?.segmentation) ? 
                                    physician?.segmentation.map((item)=> item)
                                    : physician?.segmentation
                            }
                        </Box>
                    </TableCell>
                    <TableCell className='table-row-data no-border text-align-center' rowSpan={2}>
                        <CustomButton
                            borderColor="#AD509E"
                            buttonColor="#AD509E"
                            handleClick={() => handlePhysicianProfileModal(physician?.npi_id)}
                            icon={<User color="#FFFFFF"/>}
                            title="Profile"
                            titleColor="#FFFFFF"
                        />
                    </TableCell>
                </TableRow>
                <TableRow className={index % 2 === 0 ? 'odd-row-background' : ''} key={physician.npi + '_district'}>
                    <TableCell className='table-row-data padding-0 no-border' rowspan={1}>
                        <span className='font-weight-700'>District: </span>{physician?.district}
                    </TableCell>
                </TableRow>
            </>))
        : renderNoPhysicianData();

    const physicianTable = () => <Box className="box-sizing-border-box border-1-gray br-8px">
        {/* Table Header */}

        {/* Table Content */}
        <Box>
            <Box className='padding-8px'>
                <Paper
                    className='no-box-shadow'
                    component="form"
                    sx={{
                        p: '2px 4px', display: 'flex', alignItems: 'center', width: {
                            md: 200, lg: 250, xl: 320 
                        }, border: '1px solid #D0D5DD', borderRadius: '8px' 
                    }}
                >
                    <IconButton aria-label="search" sx={{ p: '10px' }} type="button">
                        <SearchIcon />
                    </IconButton>
                    <InputBase
                        inputProps={{ 'aria-label': 'search google maps' }}
                        placeholder="Search"
                        sx={{ ml: 1, flex: 1 }}
                        onChange={(e)=>setSearchValue(e.target.value)}
                    />
                </Paper>
            </Box>
            <TableContainer className='no-box-shadow no-border' component={Paper}>
                <Table className='table-border-collapse no-border'>
                    <TableHead className="queries-table-heading">
                        <TableRow>
                            <TableCell className="table-headers-title">
                                <TableSortLabel active={orderBy === 'npi_id'}
                                    direction={orderBy === 'npi_id' ? order : 'asc'}
                                    onClick={createSortHandler('npi_id')}>
                                        NPI
                                </TableSortLabel>
                            </TableCell>
                            <TableCell className="table-headers-title">
                                <TableSortLabel active={orderBy === 'name'}
                                    direction={orderBy === 'name' ? order : 'asc'}
                                    onClick={createSortHandler('name')}>
                                        Name
                                </TableSortLabel></TableCell>
                            <TableCell className="table-headers-title-no-side-padding">
                                <TableSortLabel active={orderBy === 'location'}
                                    direction={orderBy === 'location' ? order : 'asc'}
                                    onClick={createSortHandler('location')}>
                                        Location
                                </TableSortLabel>
                            </TableCell>
                            <TableCell align='center' className="table-headers-title">
                                <TableSortLabel active={orderBy === 'segmentation'}
                                    direction={orderBy === 'segmentation' ? order : 'asc'}
                                    onClick={createSortHandler('segmentation')}>
                                        Physician Segment
                                </TableSortLabel></TableCell>
                            <TableCell align='center' className="table-headers-title">Profile</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {isLoading ? (
                            <TableRowsSkeleton
                                columns={5}
                                rows={10}
                            />
                        ) : (
                            renderPhysicianData()
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>

        {/* Table Footer */}
        { totalPages ? 
            <Box className="padding-tb-16-rl-24 border-top-gray">
                <Box className="flex-row-space-between-center">
                    <Box>
                        <CustomButton
                            buttonColor="#FFFFFF"
                            buttonDisabled={!(currentPage !== 1)}
                            customStyle={{ borderRadius: '8px' }}
                            handleClick={handlePreviousClick}
                            icon={<ArrowLeftIcon />}
                            title="Previous"
                            titleColor="#344054"
                        />
                    </Box>

                    {/* Pagination */}
                    <Box>
                        <Pagination
                            hideNextButton
                            hidePrevButton
                            count={totalPages}
                            page={currentPage}
                            shape="rounded"
                            onChange={(e, value)=>handlePageChange(e,value)}
                        />
                    </Box>

                    <Box>
                        <CustomButton
                            buttonColor="#FFFFFF"
                            buttonDisabled={!(totalPages > currentPage)}
                            customStyle={{ borderRadius: '8px' }}
                            handleClick={handleNextClick}
                            tailIcon={<ArrowRightIcon />}
                            title="Next"
                            titleColor="#344054"
                        />
                    </Box>
                </Box>
            </Box> : null}
   
    </Box>;
    return (
        <>
            <CustomAccordion
                accordionContainerBackground="bg-primary-50"
                accordionTitleColor="primary-color"
                acoordianDetailsBackground='bg-gray-50'
                byDefaultOpen={true}
                component={physicianTable()}
                title="See Full List Below"
            />
            <CustomModal component={<PhysiciansProfile handleClose={handleModal} 
                npiId={selectedNPI}/>} modalOpen={showPhysicianModal} onClose={handleModal}/>
        </>
    );
};


export default PhysiciansTable;